import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Tabs, Tab, Divider } from "@mui/material";
import { makeStyles } from "@mui/styles";
import OsterusLoader from "../../Loader";
import CitySelect from "../components/CitySelect";
import { getCostOfLiving } from "../../../actions/intelligence/CostOfLiving";
import { costOfLivingSelectors, clearState, setCities } from "../../../slices/Intelligence/CostOfLivingSlice";
import NoContent from "./NoContent";
import { addMessage } from "../../../slices/NotificationSlice/GlobalNotificationSlice";
import LivingStandard from "./Tabs/LivingStandard";
import BusinessPotential from "./Tabs/BusinessPotential";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
    },
    header: {
        width: "100%",
        position: "sticky",
        zIndex: 2,
        top: 0,
    },
    noContent: {
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    tabs: {
        "& button": {
            textTransform: "none",
            fontSize: 16,
            fontWeight: 400,
        },
        "& .MuiTabs-indicator": {
            backgroundColor: "transparent",
        },
        "& .MuiTab-root.Mui-selected": {
            color: theme.palette.additional1.main,
            fontWeight: 500,
        },
        "& .MuiTab-root": {
            minHeight: 48,
            border: "none",
        },
    },
}));

const breadcrumbComponents = {
    title: {
        text: "Cost of Living Comparison",
        color: "text.onPrimary",
    },
    breadcrumbs: [
        {
            text: "Intelligence",
            color: "textSecondary",
            link: null,
            icon: null,
        },
        {
            text: "Cost of Living Comparison",
            color: "text.selected",
            link: null,
            icon: null,
        },
    ],
};

export default function CostOfLivingComponent() {
    const data = useSelector(costOfLivingSelectors.data);
    const isLoading = useSelector(costOfLivingSelectors.loading);
    const currentCity = useSelector(costOfLivingSelectors.currentCity);
    const comparisonCity = useSelector(costOfLivingSelectors.comparisonCity);
    const currentCityError = useSelector(costOfLivingSelectors.currentCityError);
    const comparisonCityError = useSelector(costOfLivingSelectors.comparisonCityError);

    const dispatch = useDispatch();
    const classes = useStyles();

    useEffect(() => () => dispatch(clearState()), []);

    useEffect(() => {
        if (!isLoading && currentCity && comparisonCity && !data) {
            dispatch(
                addMessage({
                    type: "info",
                    title: "Oh no!",
                    message: `The city you have selected doesn't have any data to be compared. 
                    Our elves are on it, but until then - please search for a different city!`,
                    visibilityTime: 5000,
                }),
            );
        }
    }, [isLoading]);

    const handleCitiesSelected = (curCity, compCity) => {
        setTabIndex(0);
        dispatch(setCities({ currentCity: curCity, comparisonCity: compCity }));
        dispatch(getCostOfLiving({ currentCityId: curCity.id, comparisonCityId: compCity.id }));
    };

    const handleTabChanged = (e, value) => {
        setTabIndex(value);
    };

    const [tabIndex, setTabIndex] = useState(0);

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <CitySelect
                    defaultCurrentCity={currentCity}
                    defaultComparisonCity={comparisonCity}
                    currentCityError={isLoading ? false : currentCityError}
                    comparisonCityError={isLoading ? false : comparisonCityError}
                    onCitiesSelected={handleCitiesSelected}
                    breadcrumbComponents={breadcrumbComponents}
                />
            </div>
            {isLoading ? (
                <Box className={classes.noContent}>
                    <OsterusLoader />
                </Box>
            ) : data ? (
                <>
                    <Stack width="100%" px="5%" mt={1}>
                        <Tabs className={classes.tabs} value={tabIndex} onChange={handleTabChanged}>
                            <Tab value={0} label="Living Standard" />
                            <Tab value={1} label="Business Potential" />
                        </Tabs>
                        <Divider />
                    </Stack>
                    {tabIndex === 0 ? (
                        <LivingStandard data={data.livingStandard} />
                    ) : (
                        <BusinessPotential
                            data={data.businessPotential}
                            localPurchasingPower={data.livingStandard.localPurchasingPower}
                        />
                    )}
                </>
            ) : (
                <Box className={classes.noContent}>
                    <NoContent />
                </Box>
            )}
        </div>
    );
}
